import React, { useEffect, useState } from "react";
import { CPA_SECTIONS, cpAssignmentLabel } from "./cpaDsr";

const BDE_SECTIONS = [
  ["Calling Activity", [["Dials completed", "dials"], ["Connects", "connects"]]],
  ["Follow-up Activity", [["Follow-ups due", "followUpsDue"], ["Follow-ups completed", "followUpsCompleted"]]],
  ["Lead Activity", [["New leads added", "newLeads"], ["MTD leads", "newLeadsMtd"], ["YTD leads", "newLeadsYtd"]]],
  ["Pipeline Today", [["Cold", "coldLeadsToday"], ["Warm", "warmLeadsToday"], ["Hot", "hotLeadsToday"]]],
  ["Current Pipeline (MTD)", [["Cold", "coldLeadsMtd"], ["Warm", "warmLeadsMtd"], ["Hot", "hotLeadsMtd"]]],
  ["Qualified Opportunities", [["Cold to Warm", "coldToWarm"], ["Warm to Hot", "warmToHot"], ["Hot to Closed", "hotToClosed"], ["Opportunity notes", "opportunityNotes", "text"]]],
  ["Sales Activity", [["Proposals sent", "proposalsSent"], ["Negotiations in progress", "negotiations"]]],
  ["Deals Closed", [["Deals closed today", "dealsClosed"], ["ARR closed today", "arrClosed", "currency"]]],
  ["Deal Lost", [["Deals Lost", "dealLost"], ["Deal Lost Reason", "dealLostReason", "optionalText"]]],
  ["Demos and Meetings", [["Completed today", "demosCompletedToday"], ["Scheduled for future", "demosScheduledFuture"]]],
  ["Channel Partner Activity", [["Total CP", "totalCp"], ["ARR from CP", "arrFromCp", "currency"], ["CP prospects added", "cpProspects"], ["CP meetings completed", "cpMeetings"]]],
  ["Conversation Summary", [["Summary", "conversationSummary", "text"]]],
  ["Additional Notes", [["Notes", "additionalNotes", "text"]]]
];

const LGS_SECTIONS = [
  ["Lead Activity", [["New leads", "newLeads"], ["Lead Lost", "leadLost"], ["MTD leads", "newLeadsMtd"], ["YTD leads", "newLeadsYtd"], ["Call back", "callBacks"], ["Live transfer", "liveTransfers"], ["Long followup", "longFollowups"]]],
  ["Calls", [["Total calls", "outboundCalls"], ["Inbound calls", "inboundCalls"]]],
  ["Channel Partners", [["CP prospects", "cpProspects"]]],
  ["Lead Sources", [["WhatsApp", "whatsApp"], ["Referral", "referral"], ["Other", "other"]]]
];

const SR_LGS_SECTIONS = [
  ...LGS_SECTIONS,
  ["Own Follow-ups", [["Cold", "lgsFollowupCold"], ["Warm", "lgsFollowupWarm"], ["Hot", "lgsFollowupHot"]]],
  ["Own Deal Closed", [["Deals closed", "srLgsDealsClosed"], ["ARR closed", "srLgsArrClosed", "currency"]]]
];

const LGS_ONLY_SECTIONS = LGS_SECTIONS.map(([title, items]) => [
  title, title === "Calls" ? [...items, ["Connected Calls", "connectedCalls", "optionalCount"]] : items
]).concat([["Notes", [["Notes", "notes", "optionalText"]]]]);

const LEGACY_CPA_SECTIONS = BDE_SECTIONS.filter(([title]) => title !== "Deal Lost").map(([title, items]) => {
  if (title === "Lead Activity") return [title, [["New leads added", "newLeads"]]];
  if (title === "Deals Closed") return [title, [["Deals closed today", "dealsClosed"], ["ARR closed today", "arrClosed", "currency"]]];
  if (title === "Demos and Meetings") {
    return [title, [["CP Meet", "demosCompletedToday"], ["CP Meeting Scheduled", "demosScheduledFuture"]]];
  }
  return [title, items];
}).flatMap((section) =>
  section[0] === "Demos and Meetings"
    ? [section, ["Inbound Sales", [["Inbound sales / deals", "inboundDealsClosed"], ["Inbound ARR", "inboundArrClosed", "currency"]]]]
    : [section]
);

const LEGACY_LABELS = {
  leadsGenerated: "Leads Generated",
  leadsTransferred: "Leads Transferred",
  positiveResponses: "Positive Responses",
  lgsNotes: "LGS Notes",
  qualifiedOpportunities: "Qualified Opportunities",
  meetingsBooked: "Meetings Booked"
};

const EMPTY_MANAGER_REVIEW = {
  keyAchievement: "",
  biggestOpportunity: "",
  blocker: "",
  nextAction: "",
  followUpDate: "",
  plannedLeaves: "",
  upl: ""
};

function displayValue(value, type) {
  if (type === "currency") {
    return `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }
  if (type === "text") return String(value || "No notes added");
  if (type === "optionalText") return String(value || "Not provided");
  if (type === "optionalCount" && (value === undefined || value === null || value === "")) return "Not provided";
  return Number(value || 0).toLocaleString();
}

function roleLabel(role) {
  if (role === "sr_lgs") return "SR. LGS";
  if (role === "sr_bde") return "SR. BDE";
  return String(role || "").toUpperCase();
}

export default function DsrReportDetail({
  report,
  onClose,
  canManageManagerReview = false,
  onSaveManagerReview,
  savingManagerReview = false,
  embedded = false,
  preferredTab = "summary"
}) {
  const [managerReviewForm, setManagerReviewForm] = useState(EMPTY_MANAGER_REVIEW);
  const [managerReviewMessage, setManagerReviewMessage] = useState("");
  const [activeTab, setActiveTab] = useState(preferredTab === "review" ? "review" : "summary");

  useEffect(() => {
    const review = report?.managerReview || {};
    setManagerReviewForm({
      keyAchievement: review.keyAchievement || "",
      biggestOpportunity: review.biggestOpportunity || "",
      blocker: review.blocker || "",
      nextAction: review.nextAction || "",
      followUpDate: review.followUpDate || "",
      plannedLeaves: review.plannedLeaves || "",
      upl: review.upl || ""
    });
    setManagerReviewMessage("");
  }, [report?.id, report?.managerReview]);

  useEffect(() => {
    setActiveTab(preferredTab === "review" ? "review" : "summary");
  }, [preferredTab, report?.id]);

  if (!report) return null;

  const isLgs = report.dsrRole === "lgs";
  const isSrLgs = report.dsrRole === "sr_lgs";
  const isCpa = report.dsrRole === "cpa";
  const isReviewable = ["bde", "sr_bde", "lgs", "sr_lgs"].includes(report.dsrRole);
  const sections = isSrLgs ? SR_LGS_SECTIONS : isLgs ? LGS_ONLY_SECTIONS : isCpa
    ? (report.fields?.cpaFormVersion === 2 ? CPA_SECTIONS : LEGACY_CPA_SECTIONS) : BDE_SECTIONS;
  const legacyEntries = Object.entries(report.legacyFields || {});
  const showManagerReview = canManageManagerReview && isReviewable;
  const managerReview = report.managerReview || null;
  const approval = report.approval || null;
  const transferredClosedLeads = Array.isArray(report.fields?.transferredClosedLeads)
    ? report.fields.transferredClosedLeads
    : [];

  const updateManagerReviewField = (field, value) => {
    setManagerReviewForm((current) => ({
      ...current,
      [field]: value
    }));
    setManagerReviewMessage("");
  };

  const saveManagerReview = async (event) => {
    event.preventDefault();
    if (!onSaveManagerReview || savingManagerReview) return;

    try {
      setManagerReviewMessage("");
      await onSaveManagerReview(report, managerReviewForm);
      setManagerReviewMessage("TL review saved.");
    } catch (error) {
      setManagerReviewMessage(error.message || "Could not save TL review.");
    }
  };

  const summaryContent = (
    <div className="dsr-detail-grid">
      {sections.map(([title, items]) => (
        <section
          className={`dsr-detail-section${items.some(([, , type]) => type === "text" || type === "optionalText") ? " dsr-detail-section-wide" : ""}`}
          key={title}
        >
          <h3>{title}</h3>
          <div className="dsr-detail-values">
            {items.map(([label, field, type]) => (
              <div className={type === "text" || type === "optionalText" ? "dsr-detail-value-text" : ""} key={field}>
                <span>{label}</span>
                <strong>{displayValue(report.fields?.[field], type)}</strong>
              </div>
            ))}
          </div>
        </section>
      ))}

      {isCpa && <section className="dsr-detail-section dsr-detail-section-wide">
        <h3>Notes</h3>
        <div className="dsr-detail-values"><div className="dsr-detail-value-text">
          <span>Notes</span><strong>{displayValue(report.fields?.notes, "optionalText")}</strong>
        </div></div>
      </section>}

      {isCpa && (report.fields?.cpaFormVersion === 2 || report.fields?.cpAssignments?.length > 0) && (
        <section className="dsr-detail-section">
          <h3>CP Assignment</h3>
          {report.fields?.cpAssignments?.length ? <div className="dsr-detail-values">
            {report.fields.cpAssignments.map((entry, index) => <div key={`${entry.assignedToId}-${index}`}>
              <span>CP Assign To</span><strong>{cpAssignmentLabel(entry)}</strong>
            </div>)}
          </div> : <p>No assignments recorded.</p>}
        </section>
      )}

      {(isLgs || isSrLgs) && (
        <section className="dsr-detail-section dsr-detail-section-wide">
          <h3>Transferred Leads Closed</h3>
          {transferredClosedLeads.length === 0 ? (
            <p className="dsr-detail-legacy-note">No transferred lead closures added.</p>
          ) : (
            <div className="dsr-detail-values">
              {transferredClosedLeads.map((entry, index) => (
                <div key={`${entry.leadCloserId || entry.leadCloserName}-${index}`}>
                  <span>{entry.leadCloserName || "Lead closer"}</span>
                  <strong>{displayValue(entry.arrClosed, "currency")}</strong>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {approval && (
        <section className="dsr-detail-section dsr-detail-section-wide dsr-approval-audit-section">
          <h3>Low-Dial Approval</h3>
          <div className="dsr-detail-values">
            <div>
              <span>Status</span>
              <strong>{String(approval.status || "-").toUpperCase()}</strong>
            </div>
            <div>
              <span>Requested</span>
              <strong>{approval.requestedAt ? new Date(approval.requestedAt).toLocaleString() : "-"}</strong>
            </div>
            <div>
              <span>Reviewed By</span>
              <strong>{approval.reviewedBy || "-"}</strong>
            </div>
            {approval.reviewerRole && (
              <div><span>Role at Decision</span><strong>{roleLabel(approval.reviewerRole.toLowerCase().replace(".", "_"))}</strong></div>
            )}
            <div>
              <span>Decision Date</span>
              <strong>{approval.reviewedAt ? new Date(approval.reviewedAt).toLocaleString() : "-"}</strong>
            </div>
            <div className="dsr-detail-value-text">
              <span>Request Reason</span>
              <strong>{approval.requestReason || "Below DSR threshold"}</strong>
            </div>
            <div className="dsr-detail-value-text">
              <span>Approval / Rejection Reason</span>
              <strong>{approval.reviewNote || "No decision note added"}</strong>
            </div>
          </div>
        </section>
      )}

      {legacyEntries.length > 0 && (
        <section className="dsr-detail-section dsr-detail-section-wide">
          <h3>Legacy Submission Fields</h3>
          <p className="dsr-detail-legacy-note">
            Preserved from a report submitted before the current DSR format.
          </p>
          <div className="dsr-detail-values">
            {legacyEntries.map(([field, value]) => (
              <div key={field}>
                <span>{LEGACY_LABELS[field] || field}</span>
                <strong>{String(value)}</strong>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );

  const reviewContent = showManagerReview ? (
    <section className="dsr-detail-section dsr-detail-section-wide dsr-manager-review-section">
      <div className="dsr-manager-review-heading">
        <div>
          <span className="agent-dsr-eyebrow">Reviewer workspace</span>
          <h3>TL Review</h3>
          <p>Linked to this DSR, agent, reviewer, and business date. Agent-submitted fields stay unchanged.</p>
        </div>
        <span className={`dsr-review-status-badge ${managerReview ? "reviewed" : "pending"}`}>
          {managerReview ? "Reviewed" : "Review Pending"}
        </span>
      </div>

      <form className="dsr-manager-review-form" onSubmit={saveManagerReview}>
        <label>
          <span>Key Achievement Today</span>
          <textarea
            value={managerReviewForm.keyAchievement}
            onChange={(event) => updateManagerReviewField("keyAchievement", event.target.value)}
            placeholder="What was the agent's key achievement today?"
            rows={3}
          />
        </label>
        <label>
          <span>Biggest Opportunity</span>
          <textarea
            value={managerReviewForm.biggestOpportunity}
            onChange={(event) => updateManagerReviewField("biggestOpportunity", event.target.value)}
            placeholder="Mention the biggest current opportunity, prospect, value, or stage..."
            rows={3}
          />
        </label>
        <label>
          <span>Blocker</span>
          <textarea
            value={managerReviewForm.blocker}
            onChange={(event) => updateManagerReviewField("blocker", event.target.value)}
            placeholder="Mention any blocker affecting the agent or opportunity..."
            rows={3}
          />
        </label>
        <label>
          <span>Next Action</span>
          <textarea
            value={managerReviewForm.nextAction}
            onChange={(event) => updateManagerReviewField("nextAction", event.target.value)}
            placeholder="What should be the next action?"
            rows={3}
          />
        </label>
        <label>
          <span>Follow-up Date</span>
          <input
            type="date"
            value={managerReviewForm.followUpDate}
            onChange={(event) => updateManagerReviewField("followUpDate", event.target.value)}
          />
        </label>
        <label>
          <span>Planned Leaves</span>
          <input
            type="text"
            value={managerReviewForm.plannedLeaves}
            onChange={(event) => updateManagerReviewField("plannedLeaves", event.target.value)}
            placeholder="Leave planned on 29 Aug and 30 Aug"
          />
        </label>
        <label>
          <span>UPL</span>
          <input
            type="text"
            value={managerReviewForm.upl}
            onChange={(event) => updateManagerReviewField("upl", event.target.value)}
            placeholder="Optional UPL"
          />
        </label>

        <div className="dsr-manager-review-footer">
          <button type="submit" disabled={savingManagerReview}>
            {savingManagerReview ? "Saving..." : managerReview ? "Update TL Review" : "Save TL Review"}
          </button>
          {managerReviewMessage && <strong>{managerReviewMessage}</strong>}
          {managerReview && (
            <p>
              Reviewed by <b>{managerReview.updatedBy || managerReview.createdBy || "-"}</b>
              {managerReview.reviewerRole ? ` | Role at Review: ${managerReview.reviewerRole}` : ""}
              {managerReview.reviewerTeam ? ` (${managerReview.reviewerTeam})` : ""}
              {managerReview.updatedAt ? ` • Last updated ${new Date(managerReview.updatedAt).toLocaleString()}` : ""}
            </p>
          )}
        </div>
      </form>
    </section>
  ) : (
    <section className="dsr-detail-section dsr-detail-section-wide">
      <h3>Review</h3>
      {managerReview ? (
        <>
          <p>Reviewed By: <b>{managerReview.updatedBy || managerReview.createdBy}</b>
            {managerReview.reviewerRole ? ` | Role at Review: ${managerReview.reviewerRole}` : ""}
            {managerReview.updatedAt ? ` | ${new Date(managerReview.updatedAt).toLocaleString()}` : ""}
          </p>
          <div className="dsr-detail-values">
            {[["Key Achievement Today", "keyAchievement"], ["Biggest Opportunity", "biggestOpportunity"],
              ["Blocker", "blocker"], ["Next Action", "nextAction"], ["Follow-up Date", "followUpDate"],
              ["Planned Leave", "plannedLeaves"], ["UPL", "upl"]].map(([label, field]) => (
              <div key={field}><span>{label}</span><strong>{managerReview[field] || "-"}</strong></div>
            ))}
          </div>
        </>
      ) : <p className="dsr-detail-legacy-note">{isReviewable ? "Review pending" : "Review not applicable"}</p>}
    </section>
  );

  const detailShell = (
    <div className={embedded ? "dsr-detail-modal dsr-detail-drawer" : "dsr-detail-modal"}>
      <header className="dsr-detail-header">
        <div>
          <span className="agent-dsr-eyebrow">Submitted daily sales report</span>
          <h2>{report.agentName}</h2>
          <p>{roleLabel(report.dsrRole)} | {report.agentTeam || "No Team"} | {report.businessDate}</p>
        </div>
        <div className="dsr-detail-header-actions">
          <div><span>Submitted</span><strong>{report.submittedAt ? new Date(report.submittedAt).toLocaleString() : "-"}</strong></div>
          <button type="button" onClick={onClose}>{embedded ? "Close Panel" : "Close"}</button>
        </div>
      </header>

      {(showManagerReview || managerReview) && (
        <div className="dsr-detail-tabs">
          <button
            type="button"
            className={activeTab === "summary" ? "active" : ""}
            onClick={() => setActiveTab("summary")}
          >
            DSR Summary
          </button>
          <button
            type="button"
            className={activeTab === "review" ? "active" : ""}
            onClick={() => setActiveTab("review")}
          >
            Review
          </button>
        </div>
      )}

      <div className="dsr-detail-tab-body">
        {activeTab === "review" && (showManagerReview || managerReview) ? reviewContent : summaryContent}
      </div>
    </div>
  );

  if (embedded) {
    return detailShell;
  }

  return (
    <div className="dsr-detail-overlay" role="dialog" aria-modal="true" aria-label="DSR details">
      {detailShell}
    </div>
  );
}
